import React from "react";
import { CircularProgress, Popover } from "@mui/material";

// NOTE (2026-09-06): the five ClientStatus buckets (PENDING / SUBMIT_SUCCESS /
// SUBMIT_FAILED / DELIVERY_SUCCESS / DELIVERY_FAILED) are reduced to three
// rows — the SUBMIT_* names are an operator's view of the pipeline and were
// confusing in a campaign report. The two delivery outcomes keep their API
// names so the popup matches the Sent Messages Status column, and
//   Pending = Total − DELIVERY_SUCCESS − DELIVERY_FAILED
// i.e. everything the campaign contains that no delivery report has answered
// yet, whatever bucket it sits in. Derived from Total rather than summed from
// the submit buckets so the rows always add up to the Total line, even when
// Total comes from the campaign's submittedCount and the buckets lag it. (The
// old edugo backend rendered SUBMIT_SUCCESS as "Pending" too.) The legacy raw
// status_code path below still uses langMap.
const langMap = {
  // New API status codes
  // Standard statuses
  PENDING: "Pending",
  SUBMITTED: "Submitted",
  DELIVERED: "Delivered",
  FAILED: "Failed",
  EXPIRED: "Expired",
  REJECTED: "Rejected",
  INVALID: "Invalid",
  // Legacy mappings
  failed: "Failed",
  done: "Delivered",
  pending: "Pending",
  DELIVRD: "Delivered",
  visited: "Visited",
};

const SmsReport = ({
  deliveryUpdates,
  activeTrackData,
  anchorEl,
  showTrackData,
  closeTrackCampaign,
}) => {
  // Debug logs
  console.log("=== TrackCampaign Debug ===");
  console.log("deliveryUpdates:", deliveryUpdates);
  console.log("showTrackData:", showTrackData);
  console.log("activeTrackData:", activeTrackData);

  // rawLabels: the folded rows already carry their display label (Pending /
  // DELIVERY_SUCCESS / DELIVERY_FAILED) and must not go through langMap.
  const renderDeliveryDetails = (stats, rawLabels) => {
    console.log("renderDeliveryDetails stats:", stats);
    if (!stats) return null;

    // Render all statuses from the API response
    return Object.entries(stats).map(([status, count]) => (
      <p
        key={"trackCampaign_" + status}
        style={{ marginTop: 4, color: "#747474", fontSize: 14 }}
      >
        {rawLabels ? status : langMap[status] || status}
        <span style={{ float: "right", color: "#333" }}>
          {count || 0}
        </span>
      </p>
    ));
  };

  // Preferred: the customer-facing buckets (clientDeliveryStats), reduced to
  // Pending / DELIVERY_SUCCESS / DELIVERY_FAILED — see the note at the top.
  // Rows with a zero count are hidden; Total carries the campaign size.
  // Falls back to the raw status_code map (deliveryStats) and the legacy
  // { trackCampaign, urlCampaign } shape.
  const bucket = (stats, k) => Number(stats[k]) || 0;
  let trackCampaign = null;
  let urlCampaign = null;
  let isClientStats = false;

  if (showTrackData && deliveryUpdates) {
    console.log("Processing deliveryUpdates...", JSON.stringify(Object.keys(deliveryUpdates)));
    const clientStats =
      deliveryUpdates.clientDeliveryStats || deliveryUpdates.client_delivery_stats;
    if (clientStats) {
      isClientStats = true;
      const delivered = bucket(clientStats, "DELIVERY_SUCCESS");
      const failed = bucket(clientStats, "DELIVERY_FAILED");
      // Total: the campaign's own count when we have it, else every bucket.
      const total = activeTrackData?.submittedCount
        ? Number(activeTrackData.submittedCount)
        : Object.keys(clientStats).reduce((sum, k) => sum + bucket(clientStats, k), 0);
      trackCampaign = {
        Pending: Math.max(0, total - delivered - failed),
        DELIVERY_SUCCESS: delivered,
        DELIVERY_FAILED: failed,
      };
      // Zero rows say nothing — drop them (owner request 2026-09-07). Total
      // still shows the campaign size, so a missing row reads as "none".
      Object.keys(trackCampaign).forEach((k) => {
        if (!trackCampaign[k]) delete trackCampaign[k];
      });
    } else if (deliveryUpdates.deliveryStats) {
      console.log("Found deliveryStats:", deliveryUpdates.deliveryStats);
      trackCampaign = deliveryUpdates.deliveryStats;
      console.log("Set trackCampaign to:", trackCampaign);
    } else if (deliveryUpdates.trackCampaign) {
      console.log("Found legacy trackCampaign");
      trackCampaign = deliveryUpdates.trackCampaign;
      urlCampaign = deliveryUpdates.urlCampaign;
    } else {
      console.log("No tracking or trackCampaign found in deliveryUpdates");
    }
  }

  console.log("Final trackCampaign:", trackCampaign);

  // Calculate total from stats
  const getTotal = () => {
    if (activeTrackData?.submittedCount) {
      return activeTrackData.submittedCount;
    }
    if (trackCampaign) {
      return Object.values(trackCampaign).reduce((sum, val) => sum + (val || 0), 0);
    }
    return 0;
  };

  return (
    <Popover
      open
      anchorEl={anchorEl}
      anchorOrigin={{ horizontal: "left", vertical: "top" }}
      targetOrigin={{ horizontal: "right", vertical: "bottom" }}
      onClose={closeTrackCampaign}
      disableScrollLock
    >
      {showTrackData ? (
        <div style={{ padding: "16px 24px", minWidth: 250 }}>
          <p style={{ fontSize: 16, paddingBottom: 10, fontWeight: 500 }}>
            Track Campaign Report
          </p>
          <p
            style={{
              paddingBottom: 4,
              fontSize: 14,
              borderBottom: "1px solid #d4d4d3",
            }}
          >
            Total
            <span style={{ float: "right" }}>
              {getTotal()}
            </span>
          </p>
          {trackCampaign && Object.keys(trackCampaign).length > 0 ? (
            renderDeliveryDetails(trackCampaign, isClientStats)
          ) : (
            <p style={{ marginTop: 4, color: "#999", fontSize: 14 }}>No delivery data available</p>
          )}
          {urlCampaign && (
            <div style={{ marginTop: 20 }}>
              <p style={{ fontSize: 16, paddingBottom: 10, fontWeight: 500 }}>
                Track Smart URL Report
              </p>
              {Object.keys(urlCampaign).map((data) => (
                <p
                  key={"trackCampaignurl" + data}
                  style={{ marginTop: 5, color: "#747474", fontSize: 14 }}
                >
                  {langMap[data] ? langMap[data] : data}
                  <span style={{ float: "right", color: "#333" }}>
                    {urlCampaign[data]}
                  </span>
                </p>
              ))}
            </div>
          )}
          <p
            style={{
              borderTop: "1px solid #d4dee5",
              marginTop: 20,
              paddingTop: 10,
              fontSize: 12,
              wordBreak: "break-all",
            }}
          >
            <span style={{ color: "#747474" }}>Campaign Id:</span>{" "}
            {activeTrackData?.id || deliveryUpdates?.campaignId || "N/A"}
          </p>
        </div>
      ) : (
        <div style={{ padding: 20 }}>
          <CircularProgress />
        </div>
      )}
    </Popover>
  );
};

export default SmsReport;
